"use client";

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Spinner, Flex, useToast } from "@chakra-ui/react";
import { AgentConfig } from "./AgentConfig";
import { MultiPromptAgentConfig } from "./MultiPromptAgentConfig";
import { CustomLLMConfig } from "./CustomLLMConfig";
import {
  agentService,
  type Agent,
  type RetellLLM,
} from "../../voice-services/agentService";
import { useAuth } from "../../contexts/AuthContext";

const AgentRouter = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { credits, creditsLoading } = useAuth(); // Get creditsLoading state

  const [agent, setAgent] = useState<Agent | null>(null);
  const [llm, setLlm] = useState<RetellLLM | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [agentType, setAgentType] = useState<
    "single-prompt" | "multi-prompt" | "custom-llm" | null
  >(null);
  const hasMountedRef = useRef(false);
  const hasShownCreditWarning = useRef(false);

  useEffect(() => {
    if (
      !hasMountedRef.current &&
      !creditsLoading &&
      credits !== null &&
      credits < 0.01 &&
      !hasShownCreditWarning.current
    ) {
      hasShownCreditWarning.current = true;
      toast({
        title: "Insufficient Credits",
        description:
          "You need to have credits in your wallet in order to access voice agents.",
        status: "error",
        duration: 5000,
      });
      navigate("/voice-agents");
    }
    hasMountedRef.current = true;
  }, [credits, creditsLoading, navigate, toast]); // Add creditsLoading to dependencies

  useEffect(() => {
    const fetchAgentData = async () => {
      if (!agentId) {
        navigate("/voice-agents");
        return;
      }

      try {
        setIsLoading(true);
        const agentData = await agentService.get(agentId);
        setAgent(agentData);

        // Determine agent type
        if (agentData.response_engine.type === "custom-llm") {
          setAgentType("custom-llm");
        } else if (agentData.response_engine.type === "retell-llm") {
          // Fetch LLM data to determine if it's multi-prompt
          const llmData = await agentService.getRetellLLM(
            agentData.response_engine.llm_id
          );
          setLlm(llmData);

          if (llmData.states && Array.isArray(llmData.states)) {
            setAgentType("multi-prompt");
          } else {
            setAgentType("single-prompt");
          }
        }
      } catch (error) {
        console.error("Error fetching agent:", error);
        toast({
          title: "Error loading agent",
          description: "Could not load agent data",
          status: "error",
          duration: 3000,
        });
        navigate("/voice-agents");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentData();
  }, [agentId, navigate, toast]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh" bg="gray.100">
        <Spinner size="xl" color="primary.500" />
      </Flex>
    );
  }

  if (!agent) {
    return <Box>Agent not found</Box>;
  }

  // Render the appropriate component based on agent type
  if (agentType === "custom-llm") {
    return <CustomLLMConfig agent={agent} />;
  } else if (agentType === "multi-prompt") {
    return <MultiPromptAgentConfig agent={agent} />;
  } else {
    return <AgentConfig agent={agent} />;
  }
};

export default AgentRouter;
